/**
 * Document Picture-in-Picture timer window. Chromium 116+ uses
 * `documentPictureInPicture`; non-Chromium falls back to `window.open` popup.
 *
 * Children render via createPortal into the PIP doc. Use inline styles inside
 * children — copied stylesheets are best-effort and may not survive in all
 * browsers (esp. fallback popup).
 *
 * Spawned from FloatingTimerPill's "Detach" button; closes on stop / mark-out
 * (parent re-renders without `<PipTimerWindow open>`) or when the user closes
 * the popup window.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface DocumentPictureInPicture {
  requestWindow: (opts?: { width?: number; height?: number }) => Promise<Window>;
  window: Window | null;
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPicture;
  }
}

type Props = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
};

const FALLBACK_FEATURES =
  "popup=yes,width=320,height=300,resizable=yes,scrollbars=no,toolbar=no,location=no,status=no";

function isPipSupported(): boolean {
  return typeof window !== "undefined" && !!window.documentPictureInPicture;
}

function copyStyles(target: Document) {
  Array.from(document.styleSheets).forEach((sheet) => {
    try {
      const rules = sheet.cssRules;
      if (rules) {
        const style = target.createElement("style");
        style.textContent = Array.from(rules).map((r) => r.cssText).join("\n");
        target.head.appendChild(style);
      } else if (sheet.href) {
        const link = target.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        target.head.appendChild(link);
      }
    } catch {
      if (sheet.href) {
        const link = target.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        target.head.appendChild(link);
      }
    }
  });

  // Copy CSS custom properties from <html>.
  const root = document.documentElement;
  const computed = getComputedStyle(root);
  const vars: string[] = [];
  for (let i = 0; i < computed.length; i++) {
    const prop = computed.item(i);
    if (prop.startsWith("--")) {
      vars.push(`${prop}: ${computed.getPropertyValue(prop)};`);
    }
  }
  const style = target.createElement("style");
  style.textContent = `:root { ${vars.join(" ")} }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { margin: 0; padding: 0; font-family: var(--font-sans, system-ui); background: #ffffff; color: #0f172a; overflow: hidden; }
    @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
  `;
  target.head.appendChild(style);
}

export function PipTimerWindow({ open, onClose, children }: Props) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  // Stable ref so effect below doesn't re-tear when parent re-renders onClose.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let createdWin: Window | null = null;
    let pageHide: (() => void) | null = null;

    (async () => {
      try {
        if (isPipSupported()) {
          createdWin = await window.documentPictureInPicture!.requestWindow({
            width: 320,
            height: 300,
          });
        } else {
          createdWin = window.open("", "workman-timer", FALLBACK_FEATURES);
          if (!createdWin) {
            onCloseRef.current();
            return;
          }
        }
        if (cancelled) {
          createdWin?.close();
          return;
        }
        copyStyles(createdWin.document);
        const root = createdWin.document.createElement("div");
        root.id = "pip-root";
        root.style.cssText = "width:100%;height:100%;";
        createdWin.document.body.appendChild(root);
        createdWin.document.title = "Workman Timer";

        pageHide = () => onCloseRef.current();
        createdWin.addEventListener("pagehide", pageHide);

        setContainer(root);
      } catch {
        onCloseRef.current();
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (createdWin && pageHide) createdWin.removeEventListener("pagehide", pageHide);
        createdWin?.close();
      } catch {
        // ignore
      }
      setContainer(null);
    };
  }, [open]);

  if (!open || !container) return null;
  return createPortal(children, container);
}

export const PIP_SUPPORTED = isPipSupported;
